<template>
    <DefaultField :field="field" :errors="errors" :show-help-text="showHelpText">
        <template #field>
            <input
                ref="fileInput"
                class="visually-hidden"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                tabindex="-1"
                @change="handleFileSelected"
            >

            <div
                role="button"
                tabindex="0"
                class="block cursor-pointer rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/70 p-4 transition hover:border-zinc-400 focus:outline-none focus:!border-primary-500 dark:border-zinc-700 dark:bg-zinc-900/50 dark:hover:border-zinc-600 md:p-6"
                :class="{ 'border-primary-500 dark:border-primary-500': isDragging }"
                @click="openFilePicker"
                @keydown.enter.prevent="openFilePicker"
                @keydown.space.prevent="openFilePicker"
                @dragenter.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @dragover.prevent
                @drop.prevent="handleDrop"
            >
                <div
                    v-if="previewUrl"
                    class="pointer-events-none mb-4 overflow-hidden rounded-xl border border-zinc-200/80 bg-white dark:border-zinc-700 dark:bg-zinc-950"
                >
                    <img
                        :src="previewUrl"
                        :alt="field.name"
                        class="mx-auto max-h-52 w-full object-cover"
                        :style="{ aspectRatio: field.aspectRatio }"
                    >
                </div>

                <div
                    class="pointer-events-none flex flex-col items-center gap-3 text-center md:flex-row md:items-center md:gap-4 md:text-start"
                >
                    <div
                        class="panel-primary-btn inline-flex !h-9 !min-h-0 w-full items-center justify-center !px-4 !text-xs md:w-auto"
                    >
                        {{ __('Choose File') }}
                    </div>

                    <p class="text-sm font-medium leading-snug text-zinc-500 dark:text-zinc-400">
                        {{
                            previewUrl
                                ? __('Drop file or click to replace')
                                : __('Drop file or click to choose')
                        }}
                    </p>
                </div>
            </div>

            <button
                v-if="previewUrl"
                type="button"
                class="mt-3 text-sm font-semibold text-danger hover:underline"
                :disabled="isProcessing"
                @click.stop="removeImage"
            >
                {{ __('Remove') }}
            </button>

            <p v-if="cropError" class="my-2 text-danger">
                {{ cropError }}
            </p>

            <CropImageModal
                ref="cropModal"
                :open="isCropDialogOpen"
                :crop-source="cropSource"
                :is-processing="isProcessing"
                :confirm-label="cropConfirmLabel"
                @close="closeCropDialog"
                @confirm="confirmCrop"
            />
        </template>
    </DefaultField>
</template>

<script>
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { encode } from 'blurhash'
import { DependentFormField, HandlesValidationErrors } from '@/mixins'
import CropImageModal from '../../components/CropImageModal.vue'
import { resolveStorageUrl, revokeObjectUrl } from '../../utils/resolveStorageUrl'

export default {
    components: {
        CropImageModal,
    },

    mixins: [DependentFormField, HandlesValidationErrors],

    props: ['resourceName', 'resourceId', 'field'],

    data() {
        return {
            imagePath: '',
            blurhash: '',
            previewUrl: null,
            pendingFile: null,
            removed: false,
            cropSource: null,
            isCropDialogOpen: false,
            isProcessing: false,
            isDragging: false,
            cropError: null,
            cropperInstance: null,
        }
    },

    computed: {
        /**
         * Primary action label for the crop modal.
         *
         * @returns {string}
         */
        cropConfirmLabel() {
            if (this.isProcessing) {
                return this.__('Processing...')
            }

            return this.previewUrl ? this.__('Save cropped image') : this.__('Add cropped image')
        },
    },

    beforeUnmount() {
        revokeObjectUrl(this.previewUrl)
        this.destroyCropper()
    },

    watch: {
        /**
         * Initializes Cropper.js after the modal transition renders the image element.
         *
         * @param {boolean} value
         */
        isCropDialogOpen(value) {
            if (value && this.cropSource) {
                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        this.initializeCropper()
                    })
                })

                return
            }

            if (!value) {
                this.destroyCropper()
            }
        },
    },

    methods: {
        /**
         * Set the initial, internal value for the field.
         */
        setInitialValue() {
            this.imagePath = this.field.value || ''
            this.blurhash = this.field.blurhashValue || ''
            this.pendingFile = null
            this.removed = false
            this.previewUrl = resolveStorageUrl(
                this.field.previewUrl || this.field.value,
                this.field.diskUrl || '',
            )
        },

        /**
         * Opens the native file picker.
         */
        openFilePicker() {
            if (this.isProcessing) {
                return
            }

            this.$refs.fileInput?.click()
        },

        /**
         * Loads the selected file into the crop dialog.
         *
         * @param {Event} event
         */
        handleFileSelected(event) {
            const file = event.target.files?.[0]
            event.target.value = ''
            this.processFile(file)
        },

        /**
         * Handles a dropped image file.
         *
         * @param {DragEvent} event
         */
        handleDrop(event) {
            this.isDragging = false

            const file = event.dataTransfer?.files?.[0]
            this.processFile(file)
        },

        /**
         * Reads an image file and opens the crop dialog.
         *
         * @param {File|null|undefined} file
         */
        processFile(file) {
            if (!file || !file.type.startsWith('image/')) {
                return
            }

            const reader = new FileReader()

            reader.onload = () => {
                this.cropError = null
                this.cropSource = reader.result
                this.isCropDialogOpen = true
            }

            reader.readAsDataURL(file)
        },

        /**
         * Initializes the Cropper.js instance.
         */
        initializeCropper() {
            this.destroyCropper()

            const cropImage = this.$refs.cropModal?.getCropImageElement()

            if (!cropImage) {
                return
            }

            this.cropperInstance = new Cropper(cropImage, {
                aspectRatio: this.field.aspectRatio,
                viewMode: 1,
                dragMode: 'move',
                autoCropArea: 1,
                responsive: true,
            })
        },

        /**
         * Destroys the active Cropper.js instance.
         */
        destroyCropper() {
            this.cropperInstance?.destroy()
            this.cropperInstance = null
        },

        /**
         * Closes the crop dialog and resets its state.
         */
        closeCropDialog() {
            this.isCropDialogOpen = false
            this.cropSource = null
        },

        /**
         * Converts the cropped canvas to a compressed JPEG blob.
         *
         * @param {HTMLCanvasElement} canvas
         * @returns {Promise<Blob>}
         */
        canvasToBlob(canvas) {
            const maxWidth = this.field.maxWidth || 1200
            let outputCanvas = canvas

            if (canvas.width > maxWidth) {
                const scale = maxWidth / canvas.width
                outputCanvas = document.createElement('canvas')
                outputCanvas.width = maxWidth
                outputCanvas.height = Math.round(canvas.height * scale)

                const context = outputCanvas.getContext('2d')
                context.drawImage(canvas, 0, 0, outputCanvas.width, outputCanvas.height)
            }

            return new Promise((resolve, reject) => {
                outputCanvas.toBlob(
                    (blob) => blob ? resolve(blob) : reject(new Error('Unable to process image')),
                    'image/jpeg',
                    0.85,
                )
            })
        },

        /**
         * Stores the cropped image locally until the Nova form is submitted.
         */
        async confirmCrop() {
            if (!this.cropperInstance) {
                return
            }

            this.cropError = null
            this.isProcessing = true

            try {
                const canvas = this.cropperInstance.getCroppedCanvas({
                    imageSmoothingEnabled: true,
                    imageSmoothingQuality: 'high',
                })

                const blob = await this.canvasToBlob(canvas)
                const context = canvas.getContext('2d', { willReadFrequently: true })
                const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
                const blurhash = encode(imageData.data, canvas.width, canvas.height, 4, 3)

                revokeObjectUrl(this.previewUrl)

                this.pendingFile = new File([blob], 'image.jpg', { type: 'image/jpeg' })
                this.previewUrl = URL.createObjectURL(blob)
                this.blurhash = blurhash
                this.imagePath = ''
                this.removed = false

                this.markDirty()
                this.closeCropDialog()
            } catch (error) {
                this.cropError = error?.message || this.__('Unable to crop image')
            } finally {
                this.isProcessing = false
            }
        },

        /**
         * Clears the selected image.
         */
        removeImage() {
            revokeObjectUrl(this.previewUrl)
            this.pendingFile = null
            this.imagePath = ''
            this.blurhash = ''
            this.previewUrl = null
            this.removed = true
            this.markDirty()
        },

        /**
         * Fill the given FormData object with the field's internal value.
         *
         * @param {FormData} formData
         */
        fill(formData) {
            if (this.removed) {
                formData.append(this.field.attribute, '')
                formData.append(this.field.blurhashAttribute, '')

                return
            }

            if (this.pendingFile) {
                formData.append(this.field.attribute, this.pendingFile, this.pendingFile.name)
                formData.append(this.field.blurhashAttribute, this.blurhash || '')
            }
        },

        /**
         * Mark the parent Nova form as dirty.
         */
        markDirty() {
            this.$emit('field-changed')
        },
    },
}
</script>
